/*
 * Slider for sections/photo-text-rows.liquid: cards below 1000px, one row at a time from 1000px. The CSS sets
 * the layout; this file moves the track.
 *
 * - A button (the theme's carousel buttons, shown from 1000px) brings the next or previous card to the start
 *   of the track. The browser clamps the last step, so the final card ends at the right edge instead.
 * - The button at either end is disabled, which the theme's button style hides; with every card in view both
 *   are. The section also gets data-ptr-scrollable.
 * - Dots above the slider (top right), one per place the track can rest, mark the current one and jump on a
 *   click.
 * - Auto slide, when the section carries data-ptr-autoplay (seconds between slides): the next card, and back to
 *   the first after the last. A click, a press, a swipe, a sideways scroll or a key on the slider stops it, and
 *   it slides again once data-ptr-resume seconds pass without another (Dereck, 2026-09-17: every 5s, back after
 *   20s). It waits while less than half of the slider is on screen or the tab is hidden, and never runs for
 *   visitors who ask for reduced motion.
 * - Every photo in the slider loads once the slider reaches the screen, so no slide brings in an empty frame.
 * - Theme editor: selecting a Row block slides its card into view and holds the auto slide until the block is
 *   deselected.
 */
(function () {
  'use strict';

  // Card left edges and the snap line are in track coordinates (the track is position: relative, so a
  // card's offsetLeft already includes the track padding). Returns the scrollLeft that puts the target
  // card on the snap line, or null when there is no card further in that direction.
  function targetScrollLeft(cardLefts, scrollLeft, snapPadding, direction) {
    var snapLine = scrollLeft + snapPadding;
    var i;

    if (direction > 0) {
      for (i = 0; i < cardLefts.length; i++) {
        if (cardLefts[i] > snapLine + 1) {
          return cardLefts[i] - snapPadding;
        }
      }

      return null;
    }

    for (i = cardLefts.length - 1; i >= 0; i--) {
      if (cardLefts[i] < snapLine - 1) {
        return Math.max(0, cardLefts[i] - snapPadding);
      }
    }

    return null;
  }

  // Where the auto slide goes next: the next card (the browser clamps the last step to the end), or back to
  // the start once the track is at its end
  function autoplayScrollLeft(cardLefts, scrollLeft, snapPadding, maxScroll) {
    if (scrollLeft >= maxScroll - 1) {
      return 0;
    }

    var left = targetScrollLeft(cardLefts, scrollLeft, snapPadding, 1);

    return left === null ? 0 : Math.min(left, maxScroll);
  }

  // Every scrollLeft the track can rest at, one per dot: each card on the snap line, clamped to the end, so
  // cards that cannot reach the snap line (the last ones on a tablet) share the final stop
  function stopPositions(cardLefts, snapPadding, maxScroll) {
    var positions = [];

    cardLefts.forEach(function (left) {
      var position = Math.min(Math.max(0, left - snapPadding), Math.max(0, maxScroll));

      if (!positions.length || position - positions[positions.length - 1] > 1) {
        positions.push(position);
      }
    });

    return positions;
  }

  // The stop nearest the current scroll position
  function nearestStop(positions, scrollLeft) {
    var nearest = 0;

    for (var i = 1; i < positions.length; i++) {
      if (Math.abs(positions[i] - scrollLeft) < Math.abs(positions[nearest] - scrollLeft)) {
        nearest = i;
      }
    }

    return nearest;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = {
      targetScrollLeft: targetScrollLeft,
      autoplayScrollLeft: autoplayScrollLeft,
      stopPositions: stopPositions,
      nearestStop: nearestStop
    };
  }

  // Every instance of the section outputs the script tag; the listeners below need to exist once
  if (typeof document === 'undefined' || window.photoTextRowsLoaded) {
    return;
  }

  window.photoTextRowsLoaded = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return Boolean(reduceMotion && reduceMotion.matches);
  }

  function snapPaddingOf(track) {
    return parseFloat(window.getComputedStyle(track).scrollPaddingLeft) || 0;
  }

  function initSlider(section) {
    if (!section || section.hasAttribute('data-ptr-ready')) {
      return;
    }

    var track = section.querySelector('[data-ptr-track]');
    var prev = section.querySelector('[data-ptr-prev]');
    var next = section.querySelector('[data-ptr-next]');

    if (!track || !prev || !next) {
      return;
    }

    section.setAttribute('data-ptr-ready', '');

    var frame = 0;
    var cleanups = [];

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(function () {
        target.removeEventListener(type, handler, options);
      });
    }

    function cardLefts() {
      return Array.prototype.map.call(track.children, function (card) {
        return card.offsetLeft;
      });
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    var dots = section.querySelector('[data-ptr-dots]');
    var stops = [];

    // One dot per stop. The section renders one per row; they are rebuilt only when the number of stops differs
    // (a tablet, where the last cards share the final stop). The current one carries aria-current, which the
    // CSS draws as the green pill.
    function updateDots(max) {
      if (!dots) {
        return;
      }

      var positions = stopPositions(cardLefts(), snapPaddingOf(track), max);

      if (positions.length !== dots.children.length) {
        dots.textContent = '';

        positions.forEach(function (position, index) {
          var dot = document.createElement('button');

          dot.type = 'button';
          dot.className = 'ptr__dot';
          dot.setAttribute('aria-controls', track.id);
          dot.setAttribute('aria-label', 'Slide ' + (index + 1) + ' of ' + positions.length);
          dots.appendChild(dot);
        });
      }

      stops = positions;
      dots.hidden = stops.length < 2;

      var current = nearestStop(stops, track.scrollLeft);

      Array.prototype.forEach.call(dots.children, function (dot, index) {
        if (index === current) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function update() {
      frame = 0;

      var max = maxScroll();
      var scrollable = max > 1;

      section.setAttribute('data-ptr-scrollable', scrollable ? 'true' : 'false');
      prev.disabled = !scrollable || track.scrollLeft <= 1;
      next.disabled = !scrollable || track.scrollLeft >= max - 1;
      updateDots(max);
    }

    function scheduleUpdate() {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    }

    function step(direction) {
      var left = targetScrollLeft(cardLefts(), track.scrollLeft, snapPaddingOf(track), direction);

      if (left !== null) {
        track.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    listen(prev, 'click', function () {
      step(-1);
    });

    listen(next, 'click', function () {
      step(1);
    });

    if (dots) {
      listen(dots, 'click', function (event) {
        var dot = event.target.closest('.ptr__dot');
        var index = dot ? Array.prototype.indexOf.call(dots.children, dot) : -1;

        if (index >= 0 && stops[index] !== undefined) {
          track.scrollTo({ left: stops[index], behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }

    listen(track, 'scroll', scheduleUpdate, { passive: true });

    // The callback only changes the arrows, never the track's size, so it cannot loop
    var resizeObserver = null;

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(track);
    } else {
      listen(window, 'resize', scheduleUpdate);
    }

    // Auto slide

    var interval = parseFloat(section.getAttribute('data-ptr-autoplay')) * 1000 || 0;
    var resumeDelay = parseFloat(section.getAttribute('data-ptr-resume')) * 1000 || 0;
    var slideTimer = 0;
    var resumeTimer = 0;
    var stoppedByVisitor = false;
    var heldByEditor = false;
    var onScreen = false;
    var touching = false;
    var intersectionObserver = null;

    function canSlide() {
      return interval > 0 && !stoppedByVisitor && !heldByEditor && onScreen && !document.hidden &&
        !prefersReducedMotion() && maxScroll() > 1;
    }

    // Counts a full interval from now before the next slide (or cancels it when sliding is not allowed now)
    function restartCountdown() {
      window.clearTimeout(slideTimer);
      slideTimer = canSlide() ? window.setTimeout(slide, interval) : 0;
    }

    function slide() {
      slideTimer = 0;

      if (!canSlide()) {
        return;
      }

      track.scrollTo({ left: autoplayScrollLeft(cardLefts(), track.scrollLeft, snapPaddingOf(track), maxScroll()), behavior: 'smooth' });
      restartCountdown();
    }

    // The visitor used the slider: stop, and slide again once resumeDelay passes with no further use
    function stopForVisitor() {
      stoppedByVisitor = true;
      window.clearTimeout(slideTimer);
      window.clearTimeout(resumeTimer);
      slideTimer = 0;

      resumeTimer = window.setTimeout(function () {
        resumeTimer = 0;
        stoppedByVisitor = false;
        slide();
      }, resumeDelay);
    }

    if (interval > 0) {
      // Clicks and taps (the arrows included), mouse and pen presses, keys, and focus moving into the slider
      listen(section, 'click', stopForVisitor);
      listen(section, 'keydown', stopForVisitor);
      listen(section, 'focusin', stopForVisitor);
      listen(section, 'pointerdown', function (event) {
        if (event.pointerType !== 'touch') {
          stopForVisitor();
        }
      });

      // A sideways wheel or trackpad scroll over the track
      listen(track, 'wheel', function (event) {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) {
          stopForVisitor();
        }
      }, { passive: true });

      // A swipe: the track scrolls while a finger is on it. A finger that only scrolls the page moves no track.
      listen(track, 'touchstart', function () {
        touching = true;
      }, { passive: true });

      listen(track, 'touchend', function () {
        touching = false;
      }, { passive: true });

      listen(track, 'touchcancel', function () {
        touching = false;
      }, { passive: true });

      listen(track, 'scroll', function () {
        if (touching) {
          stopForVisitor();
        }
      }, { passive: true });

      listen(document, 'visibilitychange', restartCountdown);

      if (reduceMotion && reduceMotion.addEventListener) {
        listen(reduceMotion, 'change', restartCountdown);
      }

      // Theme editor: a selected Row block stays in view
      listen(section, 'shopify:block:select', function () {
        heldByEditor = true;
        restartCountdown();
      });

      listen(section, 'shopify:block:deselect', function () {
        heldByEditor = false;
        restartCountdown();
      });
    }

    // Every photo loads once the slider reaches the screen. The browser's lazy loading starts a photo only as it
    // slides into view, so an arrow or the auto slide brought in an empty frame (live page, cold cache,
    // 2026-09-17: row 4's photo arrived 0.17s into its slide).
    function loadAllPhotos() {
      Array.prototype.forEach.call(track.querySelectorAll('img[loading="lazy"]'), function (image) {
        image.loading = 'eager';
      });
    }

    if (window.IntersectionObserver) {
      intersectionObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        var wasOnScreen = onScreen;

        if (entry.isIntersecting) {
          loadAllPhotos();
        }

        onScreen = entry.intersectionRatio >= 0.5;

        if (onScreen !== wasOnScreen) {
          restartCountdown();
        }
      }, { threshold: [0, 0.5, 1] });

      intersectionObserver.observe(track);
    } else {
      loadAllPhotos();
      onScreen = true;
      restartCountdown();
    }

    // Theme editor: the section is re-rendered or removed
    section.ptrDestroy = function () {
      window.clearTimeout(slideTimer);
      window.clearTimeout(resumeTimer);
      window.cancelAnimationFrame(frame);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }

      cleanups.forEach(function (cleanup) {
        cleanup();
      });
    };

    update();
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-ptr]'), initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-ptr]'), function (section) {
      if (section.ptrDestroy) {
        section.ptrDestroy();
      }
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target;
    var track = card && card.parentElement;

    if (!track || !track.hasAttribute('data-ptr-track')) {
      return;
    }

    track.scrollTo({ left: Math.max(0, card.offsetLeft - snapPaddingOf(track)), behavior: 'smooth' });
  });
})();
